import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { Message } from '@gonfalon/messages/Message';
import { useEnvironment } from '@gonfalon/rest-api/hooks/useEnvironment';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { RequiredLabel } from '@gonfalon/ui/RequiredLabel';
import { Input, Text, TextField } from '@launchpad-ui/components';

import { environmentValidation, validation } from '#utils/confirmationUtils';
import { createEnvironment } from '#utils/environments/records/Environment';

import styles from './ExperimentEnvironmentConfirmation.module.css';

export type ExperimentEnvironmentConfirmationProps = {
  /** Called whenever the typed value's validity against the environment key/name changes. */
  onValidityChange: (isValid: boolean) => void;
};

/**
 * Copy-pasteable "Type <env> to confirm changes to this environment." field, matching the
 * experiment approval request flow. Used by the direct (non-approval) stop and save/start modals.
 */
export function ExperimentEnvironmentConfirmation({ onValidityChange }: ExperimentEnvironmentConfirmationProps) {
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();
  const { data: environment } = useEnvironment({ projectKey, environmentKey });

  const validate = environment
    ? environmentValidation(createEnvironment(environment))
    : validation('environment', { name: environmentKey });

  return (
    <TextField
      className={styles.confirmation}
      onChange={(value) => onValidityChange(validate.onValidate(value))}
      autoComplete="off"
      isRequired
    >
      <RequiredLabel isRequired>
        <Message id="experiments.environmentConfirmationDialog.environmentLabel" />
      </RequiredLabel>
      <Text slot="description" className={styles.helperText}>
        {validate.label}
      </Text>
      <Input placeholder={validate.placeholder} />
    </TextField>
  );
}
